import { io } from 'socket.io-client';
import config from 'config';

let disconnectTimeout = null;
let dispatchEditorAction = null;
let emitEvent = null;
let socket = null;

//
// actions
//
const activateAdder = ({ position }) => {
  dispatchEditorAction({ position, type: 'activateAdder' });
};

const addBlock = ({
  blockType,
  chapterId,
  courseId,
  defaultData,
  localBlockId,
  position,
  missingTranslations,
  analyticsAIActionId = null
}) => {
  dispatchEditorAction({
    blockType,
    defaultData,
    localBlockId,
    position,
    missingTranslations,
    type: 'addBlock'
  });
  emitEvent('addBlock', {
    blockType,
    chapterId,
    courseId,
    defaultData,
    localBlockId,
    position,
    analyticsAIActionId
  });
};

const clearSelectedBlocks = () => {
  dispatchEditorAction({ type: 'clearSelectedBlocks' });
};

const copyBlocks = ({ blockIds, chapterId, targetChapterId }) => {
  dispatchEditorAction({ type: 'copyBlocks' });
  emitEvent('copyBlocks', { chapterId, blockIds, targetChapterId });
};

const cropImage = ({
  blockId,
  chapterId,
  cropData,
  imageUrl,
  languageVersion
}) => {
  dispatchEditorAction({ blockId, type: 'cropImage' });
  emitEvent('cropImage', {
    blockId,
    chapterId,
    cropData,
    imageUrl,
    languageVersion
  });
};

const deactivateAdders = () => {
  dispatchEditorAction({ type: 'deactivateAdders' });
};

const deleteBlocks = ({ blockIds, chapterId }) => {
  dispatchEditorAction({ blockIds, type: 'deleteBlocks' });
  emitEvent('deleteBlocks', { blockIds, chapterId });
};

const discardChanges = ({ chapterId }) => {
  dispatchEditorAction({ type: 'discardChanges' });
  emitEvent('discardChanges', { chapterId });
};

const fetchInitialData = ({ chapterId }) => {
  emitEvent('fetchInitialData', { chapterId });
};

const hideBlocks = ({ blockIds, chapterId, visibility = 'hidden' }) => {
  dispatchEditorAction({ blockIds, visibility, type: 'hideBlocks' });
  emitEvent('hideBlocks', { blockIds, chapterId, visibility });
};

const importDocument = ({ chapterId, filePath }) => {
  dispatchEditorAction({ type: 'importDocument' });
  emitEvent('importDocument', { chapterId, filePath });
};

const markBlocksAsNew = ({ blockIds, chapterId, expirationDate }) => {
  dispatchEditorAction({ blockIds, expirationDate, type: 'markBlocksAsNew' });
  emitEvent('markBlocksAsNew', { blockIds, chapterId, expirationDate });
};

const moveBlock = ({ blockIds, chapterId, editorBlocksOrder, newPosition }) => {
  const newOrder = editorBlocksOrder.filter(
    (blockId) => !blockIds.includes(blockId)
  );
  newOrder.splice(newPosition, 0, ...blockIds);

  dispatchEditorAction({ order: newOrder, type: 'updateOrder' });
  emitEvent('updateOrder', { chapterId, order: newOrder });
  dispatchEditorAction({ type: 'clearSelectedBlocks' });
};

const publish = ({ chapterId, publishDate }) => {
  dispatchEditorAction({ publishDate, type: 'publish' });
  emitEvent('publish', { chapterId, publishDate });
};

const saveChanges = ({ chapterId }) => {
  dispatchEditorAction({ type: 'saveChanges' });
  emitEvent('saveChanges', { chapterId });
};

const selectBlock = ({ blockId, isSelected }) => {
  dispatchEditorAction({ blockId, isSelected, type: 'selectBlock' });
};

const selectBlocksMarkedAsNew = () => {
  dispatchEditorAction({ type: 'selectBlocksMarkedAsNew' });
};

const selectBlocksOfType = ({ blockType }) => {
  dispatchEditorAction({ blockType, type: 'selectBlocksOfType' });
};

const selectBlocksUnderHeading = ({ blockId }) => {
  dispatchEditorAction({ blockId, type: 'selectBlocksUnderHeading' });
};

const selectBlocksWithVisibility = ({ visibility }) => {
  dispatchEditorAction({ visibility, type: 'selectBlocksWithVisibility' });
};

const setBlockBeingDragged = ({ value }) => {
  dispatchEditorAction({ value, type: 'setBlockBeingDragged' });
};

const setBlockBeingMoved = ({ blockId }) => {
  dispatchEditorAction({ blockId, type: 'setBlockBeingMoved' });
};

const setClosestAdderIndex = ({ closestAdderIndex }) => {
  dispatchEditorAction({ closestAdderIndex, type: 'setClosestAdderIndex' });
};

const toggleImport = () => {
  dispatchEditorAction({ type: 'toggleImport' });
};

const transferBlocks = ({ blockIds, chapterId, targetChapterId }) => {
  dispatchEditorAction({ blockIds, type: 'transferBlocks' });
  emitEvent('transferBlocks', { chapterId, blockIds, targetChapterId });
};

const updateBlock = ({ blockId, data, analyticsAIActionId = null }) => {
  dispatchEditorAction({ blockId, data, type: 'updateBlock' });
  emitEvent('updateBlock', { blockId, data, analyticsAIActionId });
};

const updateBlockType = ({ blockId, blockType, data }) => {
  dispatchEditorAction({ blockId, blockType, data, type: 'updateBlockType' });
  emitEvent('updateBlockType', { blockId, blockType, data });
};

const updateTitle = ({ chapterId, title, titleLocales }) => {
  dispatchEditorAction({ title, titleLocales, type: 'updateTitle' });
  emitEvent('updateTitle', { chapterId, title, titleLocales });
};

//
// connection handlers
//
const connect = ({ chapterId, dispatch }) => {
  dispatchEditorAction = (data) => dispatch(data);

  const apiUrlOrigin = config.apiUrl.replace('/api', '');
  socket = io(`${apiUrlOrigin}/editor`, {
    auth: { chapterId },
    transports: ['websocket']
  });

  emitEvent = (type, data) => {
    try {
      if (sessionStorage) {
        sessionStorage.setItem('vuoSessionUpdated', Date.now().toString());
      }
    } catch (err) {
      console.error(err);
    }
    socket.emit(type, data);
  };

  socket.on('connect', () => {
    clearTimeout(disconnectTimeout);
    dispatch({ type: 'connect' });
  });

  socket.on('disconnect', () => {
    // use timeout because firefox dispatches the disconnect event
    // unnecessarily when moving to another page
    disconnectTimeout = setTimeout(
      () => dispatch({ type: 'disconnect' }),
      3000
    );
  });

  socket.on('error', (error) => dispatch({ type: 'error', error }));

  socket.on('message', (data) => dispatchEditorAction(data));
};

const disconnect = () => {
  if (socket) socket.disconnect();
};

export default {
  connect,
  disconnect,
  activateAdder,
  addBlock,
  clearSelectedBlocks,
  copyBlocks,
  cropImage,
  deactivateAdders,
  deleteBlocks,
  discardChanges,
  fetchInitialData,
  hideBlocks,
  importDocument,
  markBlocksAsNew,
  moveBlock,
  publish,
  saveChanges,
  selectBlock,
  selectBlocksMarkedAsNew,
  selectBlocksOfType,
  selectBlocksUnderHeading,
  selectBlocksWithVisibility,
  setBlockBeingDragged,
  setBlockBeingMoved,
  setClosestAdderIndex,
  toggleImport,
  transferBlocks,
  updateBlock,
  updateBlockType,
  updateTitle
};
